import { useState } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import { AlertCircle, Info } from '@/lib/icons';
import { adminService, type AdminCreateData } from '@/services/admin-service';
import { affiliateService } from '@/services/affiliate-service';
import {
  Input,
  Label,
  Field,
  FieldError,
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
  Button,
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  Alert,
  AlertDescription,
  toast,
} from 'prosci-ui';

interface AdminUserCreateDialogProps {
  onClose: () => void;
  onSuccess: () => void;
  canCreateSuper?: boolean;
}

export function AdminUserCreateDialog({ onClose, onSuccess, canCreateSuper = false }: AdminUserCreateDialogProps) {
  const [formData, setFormData] = useState({
    first_name: '',
    last_name: '',
    email: '',
    role: 'admin' as AdminCreateData['role'],
    affiliate_id: '',
  });
  const [errors, setErrors] = useState<Record<string, string>>({});

  // Fetch affiliates when role is affiliate or affiliate_admin
  const { data: affiliatesData } = useQuery({
    queryKey: ['affiliates'],
    queryFn: () => affiliateService.listAffiliates(),
    enabled: formData.role === 'affiliate' || formData.role === 'affiliate_admin',
  });

  const affiliates = affiliatesData?.data?.map(item => ({
    ...item.attributes,
    id: item.id
  })) || [];

  const createMutation = useMutation({
    mutationFn: (data: AdminCreateData) => adminService.createAdmin(data),
    onSuccess: (response: any) => {
      // Check if there was a warning about Auth0/password reset
      if (response?.meta?.warning) {
        toast.success('Admin user created');
        toast.error(response.meta.warning);
      } else {
        toast.success('Admin user created and password setup email sent');
      }
      onSuccess();
    },
    onError: (error: any) => {
      if (error.response?.errors) {
        // Handle validation errors from Rails
        const fieldErrors: Record<string, string> = {};
        error.response.errors.forEach((err: string) => {
          if (err.includes('Email')) {
            fieldErrors.email = err;
          } else if (err.includes('First name')) {
            fieldErrors.first_name = err;
          } else if (err.includes('Last name')) {
            fieldErrors.last_name = err;
          }
        });
        setErrors(fieldErrors);
      } else {
        setErrors({ general: error.message || 'Failed to create admin user' });
      }
    },
  });

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setErrors({});
    
    // Basic validation
    const newErrors: Record<string, string> = {};
    if (!formData.first_name.trim()) newErrors.first_name = 'First name is required';
    if (!formData.last_name.trim()) newErrors.last_name = 'Last name is required';
    if (!formData.email.trim()) newErrors.email = 'Email is required';
    if (!formData.email.includes('@')) newErrors.email = 'Invalid email format';
    
    // Validate affiliate selection for affiliate roles
    if ((formData.role === 'affiliate' || formData.role === 'affiliate_admin') && !formData.affiliate_id) {
      newErrors.affiliate_id = 'Please select a GAN account';
    }
    
    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    // Build data object, only including affiliate_id if needed
    const data: AdminCreateData = {
      first_name: formData.first_name,
      last_name: formData.last_name,
      email: formData.email,
      role: formData.role,
    };
    
    if (formData.affiliate_id && (formData.role === 'affiliate' || formData.role === 'affiliate_admin')) {
      (data as any).affiliate_id = formData.affiliate_id;
    }

    createMutation.mutate(data);
  };

  // Available roles based on permissions
  const availableRoles = canCreateSuper
    ? [
        { value: 'super', label: 'Super Admin' },
        { value: 'admin', label: 'Administrative User' },
        { value: 'data', label: 'Customer Editor' },
        { value: 'affiliate', label: 'Affiliate' },
        { value: 'affiliate_admin', label: 'Affiliate + Admin' },
      ]
    : [
        { value: 'admin', label: 'Administrative User' },
        { value: 'data', label: 'Customer Editor' },
        { value: 'affiliate', label: 'Affiliate' },
        { value: 'affiliate_admin', label: 'Affiliate + Admin' },
      ];

  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>Add Admin User</DialogTitle>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4">
          {errors.general && (
            <Alert variant="destructive">
              <AlertCircle className="h-5 w-5" />
              <AlertDescription>{errors.general}</AlertDescription>
            </Alert>
          )}

          <Alert variant="neutral">
            <Info className="h-5 w-5" />
            <AlertDescription>
              <p>New admin users will receive an email invitation to set up their password and complete Auth0 registration.</p>
              {formData.email.endsWith('@prosci.com') && (
                <p className="mt-1 font-medium">Internal Prosci users can use SSO to login.</p>
              )}
            </AlertDescription>
          </Alert>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-2 md:gap-4">
            <Field>
              <Label htmlFor="first_name" required>
                First Name
              </Label>
              <Input
                type="text"
                id="first_name"
                required
                value={formData.first_name}
                onChange={(e) => setFormData({ ...formData, first_name: e.target.value })}
                className={errors.first_name ? 'aria-invalid:border-destructive' : ''}
                aria-invalid={!!errors.first_name}
              />
              {errors.first_name && (
                <FieldError>{errors.first_name}</FieldError>
              )}
            </Field>

            <Field>
              <Label htmlFor="last_name" required>
                Last Name
              </Label>
              <Input
                type="text"
                id="last_name"
                required
                value={formData.last_name}
                onChange={(e) => setFormData({ ...formData, last_name: e.target.value })}
                className={errors.last_name ? 'aria-invalid:border-destructive' : ''}
                aria-invalid={!!errors.last_name}
              />
              {errors.last_name && (
                <FieldError>{errors.last_name}</FieldError>
              )}
            </Field>
          </div>

          <Field>
            <Label htmlFor="email" required>
              Email Address
            </Label>
            <Input
              type="email"
              id="email"
              required
              value={formData.email}
              onChange={(e) => setFormData({ ...formData, email: e.target.value.toLowerCase() })}
              className={errors.email ? 'aria-invalid:border-destructive' : ''}
              aria-invalid={!!errors.email}
            />
            {errors.email && (
              <FieldError>{errors.email}</FieldError>
            )}
          </Field>

          <Field>
            <Label htmlFor="role" required>
              Access Level
            </Label>
            <Select value={formData.role} onValueChange={(value) => setFormData({ ...formData, role: value as AdminCreateData['role'] })}>
              <SelectTrigger id="role" className="w-full">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {availableRoles.map(role => (
                  <SelectItem key={role.value} value={role.value}>
                    {role.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <p className="mt-1 text-sm text-gray-500">
              {formData.role === 'super' && 'Full system access with ability to manage other super admins'}
              {formData.role === 'admin' && 'Can manage users and content, but not super admins'}
              {formData.role === 'data' && 'Can edit customer data and portal content'}
              {formData.role === 'affiliate' && 'Limited access for affiliate partners'}
              {formData.role === 'affiliate_admin' && 'Affiliate access with administrative privileges'}
            </p>
          </Field>

          {(formData.role === 'affiliate' || formData.role === 'affiliate_admin') && (
            <Field>
              <Label htmlFor="affiliate_id" required>
                GAN Account
              </Label>
              <Select value={formData.affiliate_id} onValueChange={(value) => setFormData({ ...formData, affiliate_id: value })} required>
                <SelectTrigger id="affiliate_id" className={`w-full ${errors.affiliate_id ? 'border-destructive' : ''}`}>
                  <SelectValue placeholder="Select a GAN account..." />
                </SelectTrigger>
                <SelectContent>
                  {affiliates.map(affiliate => (
                    <SelectItem key={affiliate.id} value={affiliate.id}>
                      {affiliate.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {errors.affiliate_id && (
                <FieldError>{errors.affiliate_id}</FieldError>
              )}
            </Field>
          )}

          <DialogFooter>
            <Button
              type="button"
              onClick={onClose}
              variant="outline"
            >
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={createMutation.isPending}
            >
              {createMutation.isPending ? 'Creating...' : 'Create Admin User'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}